import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectFilter, SearchInput, ClearFilters, type Option } from '@/components/filters';
import { buildQuery } from '@/lib/filters';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2 } from 'lucide-react';

interface Activation {
  id: string;
  // camelCase (expected)
  licenseKey?: string;
  systemSignature?: string;
  platform?: string;
  activatedAt?: number;
  lastCheckIn?: number;
  // snake_case (from API)
  license_key?: string;
  license_name?: string;
  system_signature_id?: string;
  activation_token?: string;
  activated_at?: number;
  created_at?: number;
  updated_at?: number;
  last_check_in?: number;
  status: string;
  // Floating/heartbeat lease (B1.2); epoch seconds
  heartbeat_status?: string;
  next_heartbeat?: number;
  last_heartbeat?: number;
}

function Activations() {
  const { t } = useTranslation();
  const [activations, setActivations] = useState<Activation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchActivations();
  }, [search, statusFilter, page]);

  // A filter change invalidates the current page.
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const fetchActivations = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const qs = buildQuery({
        search,
        status: statusFilter,
        limit: DEFAULT_PAGE_SIZE,
        page,
      });
      const response = await fetch(`${apiUrl}/api/activations${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setActivations(data.data || []);
        setTotal(data.total ?? (data.data || []).length);
      }
    } catch (error) {
      console.error('Failed to fetch activations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/activations/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchActivations();
      }
    } catch (error) {
      console.error('Failed to revoke activation:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="License management"
        icon={CheckCircle2}
        title={t('navigation.activations')}
        subtitle={t('activations.subtitle')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('activations.allActivations')}</CardTitle>
          <CardDescription>
            {t('activations.description', { count: total || activations.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="filter-bar">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search license key, name, token or signature"
            />
            <SelectFilter
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={['UNASSIGNED', 'ASSIGNED', 'ACTIVATED'].map((v): Option => ({ value: v, label: v }))}
            />
            <ClearFilters
              onClick={() => { setSearch(''); setStatusFilter('all'); setPage(1); }}
            />
          </div>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('licenses.licenseKey')}</TableHead>
                <TableHead>{t('activations.table.systemSignature')}</TableHead>
                <TableHead>{t('activations.table.platform')}</TableHead>
                <TableHead>{t('licenses.status')}</TableHead>
                <TableHead>Lease</TableHead>
                <TableHead>{t('activations.table.activated')}</TableHead>
                <TableHead>{t('activations.table.lastCheckIn')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="empty-state">
                    <p>{t('dashboard.noData')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                activations.map((activation) => (
                  <TableRow key={activation.id}>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {activation.license_key || activation.licenseKey || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {activation.system_signature_id || activation.systemSignature || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{activation.platform || '-'}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={activation.status} />
                    </TableCell>
                    <TableCell>
                      {activation.heartbeat_status && activation.heartbeat_status !== 'NOT_STARTED' ? (
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={activation.heartbeat_status} />
                          {activation.next_heartbeat && (
                            <span className="text-xs text-muted-foreground">
                              renews {formatDate(activation.next_heartbeat * 1000)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(activation.created_at || activation.activated_at || activation.activatedAt)}
                    </TableCell>
                    <TableCell>
                      {activation.last_check_in || activation.lastCheckIn
                        ? formatDate(activation.last_check_in || activation.lastCheckIn)
                        : t('licenses.never')}
                    </TableCell>
                    <TableCell className="text-right">
                      {activation.status === 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevoke(activation.id)}
                        >
                          {t('activations.revoke')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="activations" />
        </CardContent>
      </Card>
    </div>
  );
}

export default Activations;
